import { A } from '@adornis/base/env-info.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { and } from '@adornis/validation/functions/and.js';
import { maxLength, minLength } from '@adornis/validation/functions/length.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { getAndEnsureUserDataOfUserID } from '../../../api/user.js';
import { Motivation, MWUserData } from '../../../db/user.js';
import { pruneUnfillableMotivations } from '../../../shared/motivations.js';
import { CtaPillButton } from '../../components/cta-pill-button.js';
import { translateValidationError } from '../../components/form-validation.js';
import { formatFormError, useScrollToError } from '../../components/format-form-error.js';
import { registerRouter } from './register-routes.js';
import { getJobPositionsByIDs } from '../../../api/job-position.js';

// Hand-written on purpose: `entityValidator(MWUserData)` would NOT cover this step.
// The min/max length rules live on `Motivation`, one level down, and the functional
// form walks the validator object rather than the document — so a validator built
// from MWUserData's own decorators has no `motivations` key and would skip exactly
// the field this step is about. Mirrors the @validate on `Motivation.motivation`.
const MOTIVATION_VALIDATOR = {
  motivations: {
    motivation: and(
      minLength(60, 'validation_motivation_too_short'),
      maxLength(2000, 'validation_motivation_too_long'),
    ),
  },
};

export const RegisterOnboardingMotivation = stateful(
  ({
    onFinished,
    onBack,
    jobIDs,
  }: {
    onFinished: () => void;
    onBack: () => void;
    // Explicit job set for this step instance. Steps stay mounted (hidden) after
    // navigation, so a hidden instance must not follow later rewrites of the ?jobs= param.
    jobIDs?: string[];
  }) => {
    const jobIDsCSV = jobIDs?.join(',') ?? routingStateSignal.value.searchParams.get('jobs') ?? '';
    const jobs = useQuery(
      () => getJobPositionsByIDs(jobIDsCSV.split(',').filter(Boolean))({ _id: 1, title: 1 }),
      [jobIDsCSV],
    );
    const user = useObservable(() => currentUser<User>(), []);

    const userDataQuery = useQuery(() => {
      if (!user?._id) return of(null);
      return getAndEnsureUserDataOfUserID(user._id)(selectionSet(() => MWUserData, 2));
    }, [user?._id]);

    const [saving, setSaving] = useState(false, []);
    const [saveError, setSaveError] = useState<null | string>(null, []);
    useScrollToError(saveError);

    // ensure all jobs are in motivation array
    if (jobs.value?.length) {
      jobs.value.forEach(job => {
        if (!userDataQuery.value) return;
        userDataQuery.value.motivations ??= [];
        if (!userDataQuery.value?.motivations.find(motivation => motivation.jobPositionID === job._id)) {
          userDataQuery.value?.motivations.push(
            new Motivation({ _id: A.getGloballyUniqueID(), jobPositionID: job._id, motivation: '' }),
          );
        }
      });
    }

    useEffect(() => {
      const userData = userDataQuery.value;
      if (!userData) return;
      // teardown, remove all entries that have been created empty to avoid the form from erroring in validation in the step before if pressing back
      return () => (userData.motivations = userData.motivations?.filter(motivation => !!motivation.motivation));
    }, [userDataQuery.value]);

    const [placeholder] = useState(new MWUserData({}));
    const userData = userDataQuery.value ?? placeholder;
    const { form, renderField } = useGeneratedForm(
      userData,
      {
        translationPrefix: 'motivation_form_',
        validator: MOTIVATION_VALIDATOR,
        translateError: translateValidationError,
      },
      [userData],
    );

    const handleNext = async () => {
      // Until both queries have resolved, pruning would run against an empty job set and
      // silently drop the blank-but-required visible entries, bypassing validation.
      if (!jobs.value || !userDataQuery.value) return;
      setSaveError(null);
      setSaving(true);
      try {
        const currentJobIDs = jobs.value.map(job => job._id).filter(jobID => jobID != null);
        userData.motivations = pruneUnfillableMotivations(userData.motivations ?? [], currentJobIDs);
        const err = await form.validate();
        if (err) {
          setSaveError(formatFormError(err, 'motivation_form_motivations_'));
          return;
        }
        await userData.save();
        onFinished();
      } catch (e: any) {
        console.error(e);
        setSaveError(formatFormError(e, 'motivation_form_motivations_'));
      } finally {
        setSaving(false);
      }
    };

    // Back is a "discard and go back" action — don't validate, don't save,
    // just navigate. The previous version only fired onBack on validation
    // failure (inverted logic from a stale `(await form.validate()) && onBack()`
    // pattern that lost its companion `onSuccess` save callback), which
    // effectively disabled the back button whenever the form was filled in.
    const handleBack = () => {
      // Steps no longer unmount on navigation, so the unmount-time cleanup of empty seeded
      // entries never fires anymore - strip them here so earlier steps don't trip validation.
      if (userDataQuery.value) {
        userDataQuery.value.motivations = userDataQuery.value.motivations?.filter(
          motivation => !!motivation.motivation,
        );
      }
      onBack();
    };

    // TODO (low prio) do we need real loading state and empty state handling here?
    if (!user) return nothing;

    return html`
      <div class="space-y-4">
        <div class="alert bg-base-100">${m.register_onboarding_motivation_intro_alert()}</div>
        ${
          saveError
            ? html`<div class="alert alert-error" data-error-anchor>
                <span class="icon-[material-symbols--error]"></span>
                <span>${saveError}</span>
              </div>`
            : nothing
        }
        ${repeat(userDataQuery.value?.motivations ?? [], (motivation, i) => {
          const job = jobs.value?.find(j => j._id === motivation.jobPositionID);
          if (!job) return nothing;

          return html`
            <div class="font-medium">
              <a
                class="link link-primary"
                target="_blank"
                rel="noreferrer"
                href=${registerRouter.link('jobDetail', { params: { jobPositionID: job._id } })}
              >
                ${job.title}
              </a>
            </div>
            <div class="mt-1 text-sm opacity-80">
              ${m.register_onboarding_motivation_helper_text()}<span class="text-error"> *</span>
            </div>
            ${renderField(['motivations', i, 'motivation'], TextareaInput)}
          `;
        })}
      </div>
      <div class="mt-6 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-2">
        <button class="btn w-full sm:w-auto" @click=${handleBack}>${m.back()}</button>
        ${saving ? html`<span class="loading loading-spinner"></span>` : nothing}
        ${CtaPillButton({
          label: m.register_onboarding_motivation_save_and_next_button(),
          onClick: handleNext,
          disabled: saving,
        })}
      </div>
    `;
  },
);
